import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from '../../common/utils/toast';
import {
    Settings,
    User,
    Shield,
    Database,
    LogOut,
    Save,
    ArrowLeft,
    Download,
    AlertTriangle,
    Archive,
    Instagram,
    Music2,
    FileText,
    Heart,
    Plus,
    Trash2,
    Edit2,
    Check,
    X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://brown-pelican-168360.hostingersite.com/api';

const SettingsPage = ({ user, onNavigate, setUser }) => {
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    // Parse settings JSON if present (extra profile fields stored here)
    const userSettings = typeof user?.settings === 'string' ? (() => { try { return JSON.parse(user.settings); } catch { return {}; } })() : (user?.settings || {});

    const [settings, setSettings] = useState({
        studioName: user?.studio_name || '',
        ownerName: user?.owner_name || '',
        email: user?.studio_email || user?.email || '',
        phone: user?.studio_phone || user?.phone || '',
        address: userSettings.address || user?.address || '',
        artistName: userSettings.artist_name || user?.artist_name || '',
        artistBio: userSettings.artist_bio || user?.artist_bio || '',
        instagram: userSettings.instagram || user?.instagram || '',
        tiktok: userSettings.tiktok || user?.tiktok || '',
        highRiskAlertsEnabled: userSettings.high_risk_alerts_enabled ?? user?.high_risk_alerts_enabled ?? true
    });

    // Form Segments State
    const [segments, setSegments] = useState([]);
    const [newSegment, setNewSegment] = useState({ type: 'health', title: '', content: '', is_high_risk: 0 });
    const [editingSegmentId, setEditingSegmentId] = useState(null);

    const isStudio = user.isStudio;
    const isAdmin = user.isStudio || user.role === 'superadmin';
    const isStaff = !user.isStudio && user.role !== 'superadmin';

    // Fetch form segments on mount
    const fetchSegments = React.useCallback(async () => {
        try {
            const { data } = await axios.get(`${API_URL}/form-segments?studio_id=${user?.studio_id || user?.id}`);
            setSegments(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Failed to fetch segments:", err);
        }
    }, [user]);

    useEffect(() => {
        if (isAdmin) {
            fetchSegments();
        }
    }, [isAdmin, fetchSegments]);

    const updateSetting = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));

    const handleSave = async () => {
        setLoading(true);
        try {
            const endpoint = isStaff ? `/staff/${user.id}` : `/studios/${user.id}`;
            const payload = isStaff
                ? {
                    first_name: settings.artistName.split(' ')[0],
                    last_name: settings.artistName.split(' ').slice(1).join(' '),
                    artist_name: settings.artistName,
                    artist_bio: settings.artistBio,
                    instagram: settings.instagram,
                    tiktok: settings.tiktok
                }
                : {
                    studio_name: settings.studioName,
                    owner_name: settings.ownerName,
                    studio_email: settings.email,
                    studio_phone: settings.phone,
                    address: settings.address,
                    artist_name: settings.artistName,
                    artist_bio: settings.artistBio,
                    instagram: settings.instagram,
                    tiktok: settings.tiktok,
                    high_risk_alerts_enabled: settings.highRiskAlertsEnabled
                };

            const res = await axios.put(`${API_URL}${endpoint}`, payload);

            if (res.data.success) {
                toast.success("Settings saved successfully!");
                if (setUser) {
                    setUser({ ...user, ...payload, settings: { address: settings.address, artist_name: settings.artistName, artist_bio: settings.artistBio, instagram: settings.instagram, tiktok: settings.tiktok, high_risk_alerts_enabled: settings.highRiskAlertsEnabled } });
                }
            } else {
                toast.error(res.data.message || "Error saving settings");
            }
        } catch (err) {
            toast.error("Error connecting to server");
        } finally {
            setLoading(false);
        }
    };

    const handleExportData = async () => {
        setExportLoading(true);
        try {
            setTimeout(() => {
                toast.info("Data export requested. You will receive an email once it's ready.");
                setExportLoading(false);
            }, 1500);
        } catch (err) {
            toast.error("Export failed");
            setExportLoading(false);
        }
    };

    const handleSuspendAccount = () => {
        const confirmed = window.confirm("Are you sure you want to suspend your account? This action is semi-permanent.");
        if (confirmed) {
            toast.info("Contact support to finalize account suspension.");
        }
    };

    // --- FORM SEGMENT HANDLERS ---
    const handleAddSegment = async (type) => {
        if (!newSegment.content.trim() && type === 'health') {
            toast.error("Please enter a question.");
            return;
        }
        if (type === 'agreement' && (!newSegment.title.trim() || !newSegment.content.trim())) {
            toast.error("Please enter both a title and content for the agreement.");
            return;
        }
        try {
            const payload = {
                ...newSegment,
                type,
                studio_id: user?.studio_id || user?.id || 1,
                sort_order: segments.filter(s => s.type === type).length
            };
            await axios.post(`${API_URL}/form-segments`, payload);
            setNewSegment({ type: 'health', title: '', content: '', is_high_risk: 0 });
            await fetchSegments();
        } catch (err) {
            toast.error("Failed to add segment");
        }
    };

    const handleUpdateSegment = async (id, data) => {
        try {
            await axios.put(`${API_URL}/form-segments/${id}`, data);
            setEditingSegmentId(null);
            await fetchSegments();
        } catch (err) {
            toast.error("Failed to update segment");
        }
    };

    const handleDeleteSegment = async (id) => {
        if (!window.confirm("Are you sure you want to permanently delete this?")) return;
        try {
            await axios.delete(`${API_URL}/form-segments/${id}`);
            await fetchSegments();
            toast.success("Deleted successfully.");
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to delete segment");
        }
    };

    const ToggleItem = ({ label, active, onClick }) => (
        <button
            onClick={onClick}
            className={`flex items-center gap-3 px-4 py-2 rounded-lg border-2 transition-all ${active ? 'border-[#5C5CFF] bg-[#5C5CFF]/5 text-[#5C5CFF]' : 'border-slate-100 text-slate-400'
                }`}
        >
            <div className={`w-10 h-6 rounded-full relative transition-all ${active ? 'bg-[#5C5CFF]' : 'bg-slate-200'}`}>
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${active ? 'right-1' : 'left-1'}`} />
            </div>
            <span className="text-xs font-black uppercase tracking-tight">{label}</span>
        </button>
    );

    // --- RENDER AGREEMENT TAB ---
    const renderAgreementsTab = () => {
        const agreements = segments.filter(s => s.type === 'agreement');
        return (
            <div className="space-y-6 max-w-3xl">
                <div className="flex justify-between items-start">
                    <div>
                        <h3 className="text-lg font-bold text-slate-700 mb-1">Legal Agreements & Waiver</h3>
                        <p className="text-slate-500 text-sm">Define the legal clauses clients must accept before signing. Changes sync instantly to the Consent Form.</p>
                    </div>
                </div>

                {/* Existing Agreements */}
                <div className="space-y-4">
                    {agreements.map((a, idx) => (
                        <div key={a.id} className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden group">
                            <div className="bg-white px-5 py-3 border-b border-slate-100 flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <span className="text-xs font-black text-slate-300">#{idx + 1}</span>
                                    {editingSegmentId === a.id ? (
                                        <input
                                            className="bg-transparent border-b-2 border-[#5C5CFF] outline-none text-sm font-bold text-slate-700 px-1"
                                            value={a.title}
                                            onChange={e => {
                                                const newSegments = [...segments];
                                                const index = newSegments.findIndex(s => s.id === a.id);
                                                newSegments[index].title = e.target.value;
                                                setSegments(newSegments);
                                            }}
                                            placeholder="Section Title"
                                        />
                                    ) : (
                                        <h4 className="text-sm font-bold text-slate-700">{a.title || 'Untitled Section'}</h4>
                                    )}
                                </div>
                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {(editingSegmentId === a.id) ? (
                                        <>
                                            <button onClick={() => handleUpdateSegment(a.id, a)} className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100"><Check size={14} /></button>
                                            <button onClick={() => { setEditingSegmentId(null); fetchSegments(); }} className="p-1.5 bg-slate-100 text-slate-400 rounded-lg hover:bg-slate-200"><X size={14} /></button>
                                        </>
                                    ) : (
                                        (a.studio_id === (user?.studio_id || user?.id) || user?.role === 'superadmin') && (
                                            <>
                                                <button onClick={() => setEditingSegmentId(a.id)} className="p-1.5 hover:bg-white rounded-lg text-slate-400 hover:text-[#5C5CFF] transition-colors"><Edit2 size={14} /></button>
                                                {user?.role === 'superadmin' && (
                                                    <button onClick={() => handleDeleteSegment(a.id)} className="p-1.5 hover:bg-white rounded-lg text-slate-400 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
                                                )}
                                            </>
                                        )
                                    )}
                                </div>
                            </div>
                            <div className="p-5">
                                {editingSegmentId === a.id ? (
                                    <textarea
                                        className="w-full border border-slate-200 p-3 rounded-lg text-sm focus:ring-2 focus:ring-[#5C5CFF] outline-none resize-none"
                                        rows={5}
                                        value={a.content}
                                        onChange={e => {
                                            const newSegments = [...segments];
                                            const index = newSegments.findIndex(s => s.id === a.id);
                                            newSegments[index].content = e.target.value;
                                            setSegments(newSegments);
                                        }}
                                        placeholder="Enter your legal text here..."
                                    />
                                ) : (
                                    <div className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">{a.content}</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Add New Agreement */}
                <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-6 space-y-4">
                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Add New Agreement Section</h4>
                    <input
                        className="w-full border border-slate-200 p-3 rounded-lg text-sm focus:ring-2 focus:ring-[#5C5CFF]"
                        placeholder="Section Title (e.g., Health Declaration)"
                        value={newSegment.type === 'agreement' ? newSegment.title : ''}
                        onChange={e => setNewSegment({ ...newSegment, type: 'agreement', title: e.target.value })}
                    />
                    <textarea
                        className="w-full border border-slate-200 p-3 rounded-lg text-sm h-28 focus:ring-2 focus:ring-[#5C5CFF] resize-none"
                        placeholder="Enter the legal text for this section..."
                        value={newSegment.type === 'agreement' ? newSegment.content : ''}
                        onChange={e => setNewSegment({ ...newSegment, type: 'agreement', content: e.target.value })}
                    />
                    <button
                        onClick={() => handleAddSegment('agreement')}
                        className="bg-[#5C5CFF] text-white px-5 py-2.5 rounded-lg text-xs font-bold flex items-center gap-2 hover:bg-[#4b4be6] shadow-sm"
                    >
                        <Plus size={14} /> ADD SECTION
                    </button>
                </div>
            </div>
        );
    };

    // --- RENDER HEALTH TAB ---
    const renderHealthTab = () => {
        const healthQuestions = segments.filter(s => s.type === 'health');
        return (
            <div className="space-y-6 max-w-3xl">
                <div className="flex justify-between items-start">
                    <div>
                        <h3 className="text-lg font-bold text-slate-700 mb-1">Health Screening Questions</h3>
                        <p className="text-slate-500 text-sm">Manage the questions clients must answer. Toggle "High-Risk" to alert artists to potential safety issues.</p>
                    </div>
                </div>

                {/* Existing Questions */}
                <div className="space-y-3">
                    {healthQuestions.map((q, idx) => (
                        <div key={q.id} className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex gap-4 items-start group">
                            <div className="w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center text-slate-500 font-bold text-xs shrink-0">
                                {idx + 1}
                            </div>
                            <div className="flex-1">
                                {editingSegmentId === q.id ? (
                                    <input
                                        className="w-full border border-slate-200 p-2 rounded text-sm focus:ring-2 focus:ring-[#5C5CFF]"
                                        value={q.content}
                                        onChange={e => {
                                            const newSegments = [...segments];
                                            const index = newSegments.findIndex(s => s.id === q.id);
                                            newSegments[index].content = e.target.value;
                                            setSegments(newSegments);
                                        }}
                                    />
                                ) : (
                                    <p className="text-sm text-slate-700 font-medium">{q.content}</p>
                                )}
                                <div className="flex items-center gap-3 mt-2">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={q.is_high_risk === 1}
                                            onChange={e => {
                                                const newSegments = [...segments];
                                                const index = newSegments.findIndex(s => s.id === q.id);
                                                newSegments[index].is_high_risk = e.target.checked ? 1 : 0;
                                                setSegments(newSegments);
                                                handleUpdateSegment(q.id, { ...q, is_high_risk: e.target.checked ? 1 : 0 });
                                            }}
                                            className="w-4 h-4 rounded text-[#5C5CFF] border-slate-300"
                                        />
                                        <span className="text-[10px] font-bold text-slate-500 uppercase">High-Risk Alert</span>
                                    </label>
                                    {q.is_high_risk === 1 && (
                                        <span className="text-[10px] font-bold bg-red-50 text-red-600 px-2 py-0.5 rounded border border-red-100 flex items-center gap-1">
                                            <AlertTriangle size={10} /> TRIGGERS ARTIST CHECKLIST
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                {editingSegmentId === q.id ? (
                                    <>
                                        <button onClick={() => handleUpdateSegment(q.id, q)} className="p-1.5 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100"><Check size={14} /></button>
                                        <button onClick={() => { setEditingSegmentId(null); fetchSegments(); }} className="p-1.5 bg-slate-100 text-slate-400 rounded-lg hover:bg-slate-200"><X size={14} /></button>
                                    </>
                                ) : (
                                    (q.studio_id === (user?.studio_id || user?.id) || user?.role === 'superadmin') && (
                                        <>
                                            <button onClick={() => setEditingSegmentId(q.id)} className="p-1.5 hover:bg-white rounded-lg text-slate-400 hover:text-[#5C5CFF] transition-colors"><Edit2 size={14} /></button>
                                            {user?.role === 'superadmin' && (
                                                <button onClick={() => handleDeleteSegment(q.id)} className="p-1.5 hover:bg-white rounded-lg text-slate-400 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
                                            )}
                                        </>
                                    )
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Add New Question */}
                <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-5 flex gap-4 items-center">
                    <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400 font-bold text-xs shrink-0">
                        <Plus size={16} />
                    </div>
                    <input
                        placeholder="Type a new health screening question..."
                        className="flex-1 text-sm outline-none border-none bg-transparent"
                        value={newSegment.type === 'health' ? newSegment.content : ''}
                        onChange={e => setNewSegment({ ...newSegment, type: 'health', content: e.target.value, title: 'Health Question' })}
                        onKeyDown={e => e.key === 'Enter' && handleAddSegment('health')}
                    />
                    <button
                        onClick={() => handleAddSegment('health')}
                        className="bg-emerald-600 text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 hover:bg-emerald-700"
                    >
                        <Plus size={14} /> ADD
                    </button>
                </div>

                {/* Info Box */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 items-start">
                    <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-amber-800">
                        <strong>High-Risk Alert:</strong> When a client answers "Yes" to a question marked as high-risk, the assigned artist will be notified on their dashboard with a safety checklist before starting the session.
                    </p>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6 animate-fade-in-up">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-3 sm:gap-4">
                    {onNavigate && (
                        <button
                            onClick={() => onNavigate('dashboard')}
                            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            title="Back to Dashboard"
                        >
                            <ArrowLeft size={20} className="text-slate-600" />
                        </button>
                    )}
                    <h2 className="text-xl md:text-2xl font-black text-slate-800 uppercase tracking-tight">Settings</h2>
                </div>
                <button
                    onClick={handleSave}
                    disabled={loading}
                    className="w-full sm:w-auto bg-[#5C5CFF] text-white px-6 py-2.5 rounded-md font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#4b4be6] shadow-sm disabled:opacity-50 transition"
                >
                    {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={16} />}
                    SAVE CHANGES
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                {/* Tab layout removed in favor of single page view */}

                <div className="p-4 sm:p-8 space-y-12">
                    {/* Studio Profile Section */}
                    {isAdmin && (
                        <div className="space-y-6 max-w-2xl bg-white border border-slate-200 rounded-xl p-4 sm:p-6 shadow-sm">
                            <h3 className="text-lg sm:text-xl font-black text-slate-800 border-b border-slate-100 pb-4 flex items-center gap-2">
                                <Settings className="text-[#5C5CFF]" size={20} /> Studio Profile
                                {isStaff && <span className="text-[10px] font-normal text-slate-400 bg-slate-100 px-2 py-1 rounded ml-2 uppercase tracking-widest">READ ONLY</span>}
                            </h3>
                            <div className="grid grid-cols-1 xs:grid-cols-2 gap-4 sm:gap-6">
                                <div className="col-span-2 md:col-span-1">
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Studio Name</label>
                                    <input className="w-full border border-slate-200 p-3 rounded-lg text-sm focus:ring-2 focus:ring-[#5C5CFF]" value={settings.studioName} onChange={e => updateSetting('studioName', e.target.value)} disabled={isStaff} />
                                </div>
                                <div className="col-span-2 md:col-span-1">
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Owner Name</label>
                                    <input className="w-full border border-slate-200 p-3 rounded-lg text-sm focus:ring-2 focus:ring-[#5C5CFF]" value={settings.ownerName} onChange={e => updateSetting('ownerName', e.target.value)} disabled={isStaff} />
                                </div>
                                <div className="col-span-2 md:col-span-1">
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Contact Email</label>
                                    <input className="w-full border border-slate-200 p-3 rounded-lg text-sm focus:ring-2 focus:ring-[#5C5CFF]" value={settings.email} onChange={e => updateSetting('email', e.target.value)} disabled={isStaff} />
                                </div>
                                <div className="col-span-2 md:col-span-1">
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Phone</label>
                                    <input className="w-full border border-slate-200 p-3 rounded-lg text-sm focus:ring-2 focus:ring-[#5C5CFF]" value={settings.phone} onChange={e => updateSetting('phone', e.target.value)} disabled={isStaff} />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Studio Address</label>
                                    <input className="w-full border border-slate-200 p-3 rounded-lg text-sm focus:ring-2 focus:ring-[#5C5CFF]" value={settings.address} onChange={e => updateSetting('address', e.target.value)} placeholder="122 Cornwall St, Plymouth, PL1 1NF" disabled={isStaff} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Artist Profile Section */}
                    <div className="space-y-6 max-w-2xl bg-white border border-slate-200 rounded-xl p-4 sm:p-6 shadow-sm">
                        <h3 className="text-lg sm:text-xl font-black text-slate-800 border-b border-slate-100 pb-4 flex items-center gap-2">
                            <User className="text-[#5C5CFF]" size={20} /> Artist Profile
                        </h3>
                        <div className="space-y-6">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Artist/Display Name</label>
                                <input className="w-full border border-slate-200 p-3 rounded-lg text-sm focus:ring-2 focus:ring-[#5C5CFF]" value={settings.artistName} onChange={e => updateSetting('artistName', e.target.value)} />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Bio</label>
                                <textarea className="w-full border border-slate-200 p-3 rounded-lg text-sm h-32 resize-none focus:ring-2 focus:ring-[#5C5CFF] outline-none" value={settings.artistBio} onChange={e => updateSetting('artistBio', e.target.value)} />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-2"><Instagram size={14} /> Instagram</label>
                                    <input className="w-full border border-slate-200 p-3 rounded-lg text-sm focus:ring-2 focus:ring-[#5C5CFF]" value={settings.instagram} onChange={e => updateSetting('instagram', e.target.value)} placeholder="@username" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-2"><Music2 size={14} /> TikTok</label>
                                    <input className="w-full border border-slate-200 p-3 rounded-lg text-sm focus:ring-2 focus:ring-[#5C5CFF]" value={settings.tiktok} onChange={e => updateSetting('tiktok', e.target.value)} placeholder="@username" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Agreements Section */}
                    {isAdmin && (
                        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 shadow-sm">
                            <div className="border-b border-slate-100 pb-4 mb-6 flex items-center gap-2">
                                <FileText className="text-[#5C5CFF]" size={20} />
                                <h3 className="text-lg sm:text-xl font-black text-slate-800">Legal Agreements</h3>
                            </div>
                            {renderAgreementsTab()}
                        </div>
                    )}

                    {/* Health Questions Section */}
                    {isAdmin && (
                        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 shadow-sm">
                            <div className="border-b border-slate-100 pb-4 mb-6 flex items-center gap-2">
                                <Heart className="text-[#5C5CFF]" size={20} />
                                <h3 className="text-lg sm:text-xl font-black text-slate-800">Health Questions</h3>
                            </div>
                            {renderHealthTab()}
                        </div>
                    )}

                    {/* Safety Settings Section */}
                    {isAdmin && (
                        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 shadow-sm max-w-2xl">
                            <div className="border-b border-slate-100 pb-4 mb-6 flex items-center gap-2">
                                <Shield className="text-[#5C5CFF]" size={20} />
                                <h3 className="text-lg sm:text-xl font-black text-slate-800">Safety Settings</h3>
                            </div>
                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 sm:p-6">
                                <div className="flex flex-col sm:flex-row items-start gap-4">
                                    <AlertTriangle className="text-amber-600 mt-1 shrink-0" size={24} />
                                    <div>
                                        <h4 className="font-bold text-slate-800 mb-1">High-Risk Health Alerts</h4>
                                        <p className="text-sm text-slate-600 mb-4">Enable alerts for blood thinners, skin conditions, etc.</p>
                                        <ToggleItem
                                            label={settings.highRiskAlertsEnabled ? "Enabled" : "Disabled"}
                                            active={settings.highRiskAlertsEnabled}
                                            onClick={() => updateSetting('highRiskAlertsEnabled', !settings.highRiskAlertsEnabled)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Data Management Section */}
                    {isAdmin && (
                        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 shadow-sm max-w-2xl">
                            <div className="border-b border-slate-100 pb-4 mb-6 flex items-center gap-2">
                                <Database className="text-[#5C5CFF]" size={20} />
                                <h3 className="text-lg sm:text-xl font-black text-slate-800">Data Management</h3>
                            </div>
                            <div className="bg-slate-50 p-4 sm:p-6 rounded-xl border border-slate-200">
                                <h4 className="font-bold text-slate-800 mb-2">Export All Data</h4>
                                <p className="text-sm text-slate-600 mb-4">Download your data in ZIP format (Forms, Clients, Logs).</p>
                                <button onClick={handleExportData} disabled={exportLoading} className="w-full sm:w-auto bg-slate-800 text-white px-6 py-2 rounded-lg font-bold text-sm flex items-center justify-center gap-2 shadow-sm transition">
                                    {exportLoading ? "Preparing..." : <><Download size={16} /> Request ZIP Export</>}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Account Section */}
                    {isAdmin && (
                        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6 shadow-sm max-w-2xl">
                            <div className="border-b border-slate-100 pb-4 mb-6 flex items-center gap-2">
                                <LogOut className="text-[#5C5CFF]" size={20} />
                                <h3 className="text-lg sm:text-xl font-black text-slate-800">Account</h3>
                            </div>
                            <div className="bg-red-50 p-4 sm:p-6 rounded-xl border border-red-200">
                                <h4 className="font-bold text-red-800 mb-2 flex items-center gap-2"><Archive size={18} /> Suspend Account</h4>
                                <p className="text-sm text-red-600 mb-4">Forms are retained for 3 years legally even if suspended.</p>
                                <button onClick={handleSuspendAccount} className="w-full sm:w-auto bg-red-600 text-white px-6 py-2 rounded-lg font-bold text-sm hover:bg-red-700 shadow-sm transition">Suspend & Archive</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;
